/**
 * AI 用量的展示格式化。
 *
 * 所有指标都可能为空：老数据、以及不回 usage 的 OpenAI 兼容端点都没有 token 记录，
 * 这种情况统一显示占位符而不是 0——0 会被误读成「真的没消耗」。
 */

export const EMPTY_PLACEHOLDER = '—';

function isBlank(value?: number | null): boolean {
  return value === null || value === undefined || Number.isNaN(value);
}

/**
 * token 数按量级缩写：1.2K / 3.4M / 1.2B。
 * 千位以下保持原样——「872」比「0.9K」更有信息量。
 */
export function formatTokens(value?: number | null): string {
  if (isBlank(value)) {
    return EMPTY_PLACEHOLDER;
  }
  const num = value as number;
  const abs = Math.abs(num);
  if (abs < 1_000) {
    return String(num);
  }
  const units: Array<[number, string]> = [
    [1_000_000_000, 'B'],
    [1_000_000, 'M'],
    [1_000, 'K'],
  ];
  const [scale, suffix] = units.find(([threshold]) => abs >= threshold) as [number, string];
  const scaled = num / scale;
  // 10 以下保留一位小数（1.2K），再大就取整（12K）——两位数带小数太吵
  return `${Math.abs(scaled) < 10 ? scaled.toFixed(1) : Math.round(scaled)}${suffix}`;
}

/** 精确值，给 tooltip 用（缩写会丢精度） */
export function formatExactTokens(value?: number | null): string {
  return isBlank(value) ? EMPTY_PLACEHOLDER : (value as number).toLocaleString('en-US');
}

/** 毫秒 → 人类可读；不足 1 秒保留毫秒，超过 1 分钟拆成分秒 */
export function formatDuration(ms?: number | null): string {
  if (isBlank(ms)) {
    return EMPTY_PLACEHOLDER;
  }
  const value = ms as number;
  if (value < 1000) {
    return `${value} ms`;
  }
  const seconds = value / 1000;
  if (seconds < 60) {
    return `${seconds.toFixed(1)} s`;
  }
  const minutes = Math.floor(seconds / 60);
  const rest = Math.round(seconds % 60);
  return `${minutes} m ${rest} s`;
}

/**
 * 缓存利用率 = 命中缓存的输入 token / 全部输入 token。
 * 输入为 0 时没有可谈的利用率（不是 0%），返回 null。
 */
export function cacheHitRatio(cached?: number | null, prompt?: number | null): number | null {
  if (isBlank(cached) || isBlank(prompt) || (prompt as number) <= 0) {
    return null;
  }
  return (cached as number) / (prompt as number);
}

export function formatCacheHitRate(cached?: number | null, prompt?: number | null): string {
  const ratio = cacheHitRatio(cached, prompt);
  return ratio === null ? EMPTY_PLACEHOLDER : `${(ratio * 100).toFixed(1)}%`;
}
